import React, { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit2, X, User, Building2, Search, ChevronDown } from 'lucide-react';

const SuspectTab = ({ caseId, assignedTo, user }) => {
  const [suspects, setSuspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSuspect, setEditingSuspect] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [inmates, setInmates] = useState([]);
  const [loadingInmates, setLoadingInmates] = useState(false);
  const [inmateSearchQuery, setInmateSearchQuery] = useState('');
  const [showInmateDropdown, setShowInmateDropdown] = useState(false);
  const inmateDropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    suspectType: 'Inmate',
    facilityId: '',
    facilityName: '',
    inmateId: '',
    inmateName: '',
    customerName: '',
    customerId: '',
    phoneNumber: ''
  });

  useEffect(() => {
    fetchSuspects();
    fetchFacilities();
  }, [caseId]);

  // Handle click outside to close inmate dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inmateDropdownRef.current && !inmateDropdownRef.current.contains(event.target)) {
        setShowInmateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuspects = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/suspects/case/${caseId}`);
      setSuspects(response.data.data || []);
    } catch (error) {
      console.error('Error fetching suspects:', error);
      toast.error('Failed to load suspects');
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to load facilities');
    }
  };

  const fetchInmates = async (facilityId) => {
    if (!facilityId) {
      setInmates([]);
      return [];
    }
    
    try {
      setLoadingInmates(true);
      const response = await api.get(`/inmates/facility/${facilityId}`);
      const fetchedInmates = response.data.data || [];
      setInmates(fetchedInmates);
      return fetchedInmates;
    } catch (error) {
      console.error('Error fetching inmates:', error);
      toast.error('Failed to load inmates');
      setInmates([]);
      return [];
    } finally {
      setLoadingInmates(false);
    }
  };

  const canEdit = user?.role === 'Administrator' || 
                 user?.role === 'Supervisor' || 
                 (user?.role === 'Investigator' && (assignedTo?.id === user?.id || assignedTo?._id === user?._id));

  const handleFacilityChange = (e) => {
    const facilityId = e.target.value;
    // Compare as strings to handle type mismatches
    const selectedFacility = facilities.find(f => String(f.id) === String(facilityId));
    
    setFormData({
      ...formData,
      facilityId: facilityId || '',
      facilityName: selectedFacility?.name || '',
      inmateId: '',
      inmateName: ''
    });
    
    setInmateSearchQuery('');
    setShowInmateDropdown(false);
    
    if (facilityId) {
      fetchInmates(facilityId);
    } else {
      setInmates([]);
    }
  };

  // Filter inmates based on search query (name or code)
  const filteredInmates = inmates.filter(inmate => {
    if (!inmateSearchQuery.trim()) return true;
    const query = inmateSearchQuery.toLowerCase().trim();
    const name = (inmate.inmateName || '').toLowerCase();
    // Convert code to string for comparison (handles both string and numeric codes)
    const code = String(inmate.inmateCode || '').toLowerCase();
    const displayName = (inmate.inmateDisplayName || '').toLowerCase();
    return name.includes(query) || code.includes(query) || displayName.includes(query);
  });

  const handleInmateSelect = (inmate) => {
    setFormData({
      ...formData,
      inmateId: inmate.inmateCode,
      inmateName: inmate.inmateName
    });
    setInmateSearchQuery(inmate.inmateDisplayName || `${inmate.inmateName} (${inmate.inmateCode})`);
    setShowInmateDropdown(false);
  };

  const handleInmateSearchChange = (e) => {
    const value = e.target.value;
    setInmateSearchQuery(value);
    setShowInmateDropdown(true);
    
    // If there's a selected inmate and user is clearing the search, clear selection
    if (!value && formData.inmateId) {
      setFormData({
        ...formData,
        inmateId: '',
        inmateName: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inmate selection if suspect type is Inmate
    if (formData.suspectType === 'Inmate') {
      if (!formData.facilityId || !formData.facilityName) {
        toast.error('Please select a facility');
        return;
      }
      if (!formData.inmateId || !formData.inmateName) {
        toast.error('Please select an inmate');
        return;
      }
    }
    
    // Prepare form data with trimmed values
    const submitData = {
      ...formData,
      inmateId: formData.inmateId ? String(formData.inmateId).trim() : '',
      inmateName: formData.inmateName ? String(formData.inmateName).trim() : '',
      facilityId: formData.facilityId ? String(formData.facilityId).trim() : '',
      facilityName: formData.facilityName ? String(formData.facilityName).trim() : '',
      customerName: formData.customerName ? String(formData.customerName).trim() : '',
      customerId: formData.customerId ? String(formData.customerId).trim() : '',
      phoneNumber: formData.phoneNumber ? String(formData.phoneNumber).trim() : ''
    };
    
    try {
      if (editingSuspect) {
        // Update existing suspect
        await api.put(`/suspects/${editingSuspect.id}`, submitData);
        toast.success('Suspect updated successfully');
      } else {
        // Create new suspect
        await api.post('/suspects', {
          ...submitData,
          caseId
        });
        toast.success('Suspect added successfully');
      }
      
      resetForm();
      fetchSuspects();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save suspect');
    }
  };

  const handleEdit = async (suspect) => {
    setEditingSuspect(suspect);
    setFormData({
      suspectType: suspect.suspectType,
      facilityId: suspect.facilityId || '',
      facilityName: suspect.facilityName || '',
      inmateId: suspect.inmateId || '',
      inmateName: suspect.inmateName || '',
      customerName: suspect.customerName || '',
      customerId: suspect.customerId || '',
      phoneNumber: suspect.phoneNumber || ''
    });
    
    if (suspect.suspectType === 'Inmate' && suspect.facilityId) {
      const fetchedInmates = await fetchInmates(suspect.facilityId);
      // Find the selected inmate from the fetched data
      const selectedInmate = fetchedInmates.find(i => i.inmateCode === suspect.inmateId);
      if (selectedInmate) {
        setInmateSearchQuery(selectedInmate.inmateDisplayName || `${selectedInmate.inmateName} (${selectedInmate.inmateCode})`);
      } else {
        setInmateSearchQuery(suspect.inmateName ? `${suspect.inmateName} (${suspect.inmateId})` : '');
      }
    } else {
      setInmateSearchQuery('');
    }
    
    setShowAddForm(true);
  };

  const handleDelete = async (suspect) => {
    if (!window.confirm(`Are you sure you want to remove this ${suspect.suspectType.toLowerCase()} suspect?`)) {
      return;
    }
    
    try {
      await api.delete(`/suspects/${suspect.id}`);
      toast.success('Suspect removed successfully');
      fetchSuspects();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove suspect');
    }
  };

  const resetForm = () => {
    setFormData({
      suspectType: 'Inmate',
      facilityId: '',
      facilityName: '',
      inmateId: '',
      inmateName: '',
      customerName: '',
      customerId: '',
      phoneNumber: ''
    });
    setEditingSuspect(null);
    setShowAddForm(false);
    setInmates([]);
    setInmateSearchQuery('');
    setShowInmateDropdown(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Suspect Information</h3>
        {canEdit && (
          <button
            onClick={() => {
              if (showAddForm) {
                resetForm();
              } else {
                setShowAddForm(true);
              }
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {showAddForm ? (
              <>
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Add Suspect</span>
              </>
            )}
          </button>
        )}
      </div>

      {showAddForm && canEdit && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">
            {editingSuspect ? 'Edit Suspect' : 'Add New Suspect'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suspect Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.suspectType}
                onChange={(e) => {
                  setFormData({ ...formData, suspectType: e.target.value });
                  if (e.target.value === 'Customer') {
                    setInmates([]);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="Inmate">Inmate</option>
                <option value="Customer">Customer</option>
              </select>
            </div>

            {formData.suspectType === 'Inmate' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facility <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.facilityId}
                    onChange={handleFacilityChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a facility</option>
                    {facilities.map((facility) => (
                      <option key={facility.id} value={facility.id}>
                        {facility.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.facilityId && (
                  <div className="relative" ref={inmateDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Inmate <span className="text-red-500">*</span>
                    </label>
                    {loadingInmates ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={inmateSearchQuery}
                            onChange={handleInmateSearchChange}
                            onFocus={() => setShowInmateDropdown(true)}
                            placeholder="Search by name or code..."
                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {inmateSearchQuery && (
                            <button
                              type="button"
                              onClick={() => {
                                setInmateSearchQuery('');
                                setFormData({ ...formData, inmateId: '', inmateName: '' });
                                setShowInmateDropdown(false);
                              }}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                              <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            </button>
                          )}
                        </div>
                        {showInmateDropdown && filteredInmates.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {filteredInmates.map((inmate) => (
                              <div
                                key={inmate.inmateCode}
                                onClick={() => handleInmateSelect(inmate)}
                                className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="text-sm text-gray-900">
                                  {inmate.inmateDisplayName || `${inmate.inmateName} (${inmate.inmateCode})`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {showInmateDropdown && inmateSearchQuery && filteredInmates.length === 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            <div className="px-4 py-2 text-sm text-gray-500">
                              No inmates found matching "{inmateSearchQuery}"
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {!loadingInmates && inmates.length === 0 && formData.facilityId && (
                      <p className="mt-1 text-sm text-gray-500">No inmates found for this facility</p>
                    )}
                  </div>
                )}

                {formData.inmateId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Selected Inmate:</span> {formData.inmateName} ({formData.inmateId})
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      <span className="font-medium">Facility:</span> {formData.facilityName}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer ID
                  </label>
                  <input
                    type="text"
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingSuspect ? 'Update Suspect' : 'Add Suspect'}
              </button>
            </div>
          </form>
        </div>
      )}

      {suspects.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No suspects added yet</p>
          {canEdit && (
            <p className="text-sm text-gray-400 mt-2">Click "Add Suspect" to get started</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {suspects.map((suspect) => (
            <div
              key={suspect.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    {suspect.suspectType === 'Inmate' ? (
                      <Building2 className="w-5 h-5 text-blue-600" />
                    ) : (
                      <User className="w-5 h-5 text-green-600" />
                    )}
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      suspect.suspectType === 'Inmate' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {suspect.suspectType}
                    </span>
                  </div>

                  {suspect.suspectType === 'Inmate' ? (
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Inmate Name:</span>
                        <span className="ml-2 text-sm text-gray-900">{suspect.inmateName}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Inmate ID:</span>
                        <span className="ml-2 text-sm text-gray-900">{suspect.inmateId}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">Facility:</span>
                        <span className="ml-2 text-sm text-gray-900">{suspect.facilityName}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-500">Customer Name:</span>
                        <span className="ml-2 text-sm text-gray-900">{suspect.customerName}</span>
                      </div>
                      {suspect.customerId && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Customer ID:</span>
                          <span className="ml-2 text-sm text-gray-900">{suspect.customerId}</span>
                        </div>
                      )}
                      {suspect.phoneNumber && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Phone Number:</span>
                          <span className="ml-2 text-sm text-gray-900">{suspect.phoneNumber}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {canEdit && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(suspect)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit suspect"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(suspect)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove suspect"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuspectTab;

